import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  EventEmitter,
  Input,
  Output,
  ElementRef,
  ViewChild,
  ChangeDetectorRef,
  OnDestroy,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ZonePolygon } from 'src/app/modules/shared/viewModels/zonePolygon';
import { GeoLocationService } from 'src/app/modules/shared/services/geo-location.service';
import { GMapUtilities } from 'src/app/modules/shared/utilities/gMap.utilities';
import { ShapePointViewModel } from 'src/app/modules/shared/viewModels/shapePointViewModel';
import { BaseMap } from 'src/app/modules/shared/interfaces/map/baseMapInterface';
import { ZoneViewModel } from 'src/app/modules/settings/viewModels/zoneViewModel';
import { AreaViewModel } from 'src/app/modules/settings/viewModels/areaViewModel';
import { What3WordInfo } from 'src/app/modules/incidents/models/what3WordInfo';
import { PolygonType } from 'src/app/modules/shared/enums/maps/polygonType';
import { SearchFieldComponent } from '../controls/search-field/search-field.component';
import { IncidentsSettingsService } from 'src/app/modules/shared/services/indicents-settings.service';
import { AreaPolygon } from 'src/app/modules/shared/viewModels/areaPolygon';
import { IPolygonLike } from 'src/app/modules/shared/interfaces/polygonLike.interface';
import { LocalisationService } from 'src/app/modules/shared/services/localisation.service';
import { MapsLoaderService } from 'src/app/modules/shared/services/mapsLoader.service';
import { VenueMapsService } from 'src/app/modules/settings/services/venue-maps.service';
import { IVenueMapViewModel } from 'src/app/modules/shared/interfaces/map/venueMapViewModel.interface';
import { DarkOverlayMapType } from 'src/app/modules/shared/models/map/overlays/darkOverlayMapType';
import { take, timer } from 'rxjs';
import { CachingService } from 'src/app/modules/shared/services/caching.service';
import { IVenueMapOverlay } from 'src/app/modules/shared/models/map/overlays/venueMapOverlay.interface';
import { MapSearchType } from 'src/app/modules/shared/enums/maps/mapSearchType';
import { UrlService } from 'src/app/modules/shared/services/url.service';
import { ExternalService } from 'src/app/modules/external/services/external.service';
import { ExternalAuthService } from 'src/app/modules/external/services/external-auth.service';

@Component({
    selector: 'app-zone-map',
    templateUrl: './zone-map.component.html',
    styleUrls: ['./zone-map.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ZoneMapComponent extends BaseMap implements OnInit, OnDestroy, OnChanges {
  @ViewChild('map', { static: true }) mapElement: ElementRef<HTMLElement>;
  @ViewChild('topLeftControlsWrapper') topLeftControlsWrapper: ElementRef<HTMLDivElement>;
  @ViewChild('topCenterControlWrapper') topCenterControlsWrapper: ElementRef<HTMLDivElement>;
  @ViewChild('bottomCenterControlsWrapper') bottomCenterControlsWrapper: ElementRef<HTMLDivElement>;
  @ViewChild('searchControl') searchControlComponent: SearchFieldComponent;
  @ViewChild('myLocationControlBtnWrapper') myLocationControlBtnWrapper: ElementRef<HTMLDivElement>;
  @ViewChild('topRightControlsWrapper') topRightControlsWrapper: ElementRef<HTMLDivElement>;

  // Input properties
  @Input() lat: number;
  @Input() lng: number;
  @Input() defaultZoom: number;

  /** This property is used for editing mode. When it is not null the map displays the polygon in editing state and therefore a new polygon can not be added */
  @Input() polygonInput: IPolygonLike = null;

  /** Indicator if the newly created polygon is a zone or area. */
  @Input() polygonDrawingMode: PolygonType = PolygonType.ZONE;

  /** Indicator weather the zones and areas to be rendered on the map when the map tiles get loaded. */
  @Input() displayZonesUponLoad = true;

  @Input() overlayOpacity: number = 0.75;

  // Output
  @Output() zonePolygonUpdated: EventEmitter<IPolygonLike> = new EventEmitter<IPolygonLike>();

  private static readonly MIN_POLYGON_VERTEX: number = 3;

  /** A callback function to be executed upon the creation of the zones polygons. */
  private onZonesPolygonCreated: () => void = null;

  private drawingManager: google.maps.drawing.DrawingManager;

  public drawablesLoaded = false;
  public mapTilesLoaded = false;

  public zoom: number;
  public searchControl: UntypedFormControl;
  public newDrawingZonePolygon: google.maps.Polygon = null;
  public focusZoneId: number;

  public mapControlsReady = false;

  disableMVCArraySetAtEventSubmitter: boolean = false;

  //Zones and areas view models represented by the response of the backend endpoint.
  zones: ZoneViewModel[] = [];
  areas: AreaViewModel[] = [];
  venueMaps: IVenueMapViewModel[] = [];

  constructor(
    private changeDetector: ChangeDetectorRef,
    protected readonly cachingService: CachingService,
    protected readonly geoLocationService: GeoLocationService,
    protected readonly localisationService: LocalisationService,
    protected readonly mapsLoaderService: MapsLoaderService,
    protected readonly incidentsSettingsService: IncidentsSettingsService,
    protected readonly venueMapsService: VenueMapsService,
    protected readonly urlService: UrlService,
    protected readonly externalService: ExternalService,
    protected readonly externalAuthService: ExternalAuthService
  ) {
    super(
      cachingService,
      mapsLoaderService,
      geoLocationService,
      localisationService,
      venueMapsService,
      incidentsSettingsService,
      urlService,
      externalService,
      externalAuthService
    );
  }

  ngOnInit() {
    this.zoom = this.defaultZoom || 1;
    if(this.polygonInput) {
      this.polygonDrawingMode = (this.polygonInput instanceof ZonePolygon)
        ? PolygonType.ZONE
        : PolygonType.AREA;
    }

    this.initMap();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['overlayOpacity'] && this.map) {
      this.updateOverlayOpacity(changes['overlayOpacity'].currentValue);
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  get gmap(): google.maps.Map {
    return this.map;
  }

  get isMobile(): boolean {
    return super.isMobile;
  }

  get enableZonesAreasStateChange(): boolean {
    return !this.polygonInput;
  }

  protected initMap() {
    const mapOptions = {
      mapTypeControl: false
    };

    this.subscriptions.push(
      this.googleMapsAPIs.subscribe(() => {
        super.initMap(this.mapElement, mapOptions);
        this.registerMapEvents();
      })
    );
  }

  private registerMapEvents(): void {
    google.maps.event.addListenerOnce(this.map, 'idle', () => {
      this.mapTilesLoaded = true;

      this.enableGroundOverlayBrightnessControl();
      this.initControlsOverlay();
      this.processZonesAndAreas();
      this.initDrawingManager();
      this.initOverlayTiles();
      this.changeDetector.detectChanges();
    });
  }

  // Override - Used in IncidentDetailsComponent to reset the location
  setComponentLocation(lat: number, lng: number) {
    this.lat = lat;
    this.lng = lng;
    this.zoom = 10;
  }

  public onDrawingStateChanged(drawingState: boolean) {
    if(!drawingState) {
      this.drawingManager.setDrawingMode(null);
      return;
    }

    // Trigger a click event to ensure the map has focus.
    // This is necessary to because the drawing manager is sometimes unreliable.
    // (in this case the focus is lost after selecting a search result from the autocomplete control)
    google.maps.event.trigger(this.map, 'click', { latLng: this.map.getCenter() });
    this.drawingManager.setDrawingMode(google.maps.drawing.OverlayType.POLYGON);
  }

  public onCancelDrawingPolygonClicked() {
      if (this.newDrawingZonePolygon) {
        this.newDrawingZonePolygon.setMap(null);
        this.newDrawingZonePolygon = null;
      }

      this.zonePolygonUpdated.emit(this.getPolygonViewModel(null));
  }

  public panToZone(zoneId: number) {
    const zonePolygon = this.zonePolygonMap.get(zoneId);
    if (zonePolygon) {
      const latLngBounds = GMapUtilities.getPolygonLatLngBounds(zonePolygon);
      this.focusZoneId = zoneId;
      this.map.fitBounds(latLngBounds);
    } else {
      this.onZonesPolygonCreated = () => {
        if(this.zonePolygonMap.get(zoneId)) {
          this.focusZoneId = zoneId;
          this.map.fitBounds(GMapUtilities.getPolygonLatLngBounds(
            this.zonePolygonMap.get(zoneId)
          ));
        }
      };
    }
  }

  public onMyLocationAvailable($latLng: google.maps.LatLng): void {
    this.myLocationLatLng = $latLng;
    this.map.panTo(this.myLocationLatLng);
    this.map.setZoom(BaseMap.MY_LOCATION_DEFAULT_ZOOM);
  }

  public onSearchTypeChanged($searchType: MapSearchType): void {
    this.onVenueMapSelectionCleared();
  }

  public onSearchStateChanged($expanded: boolean): void {
    if ($expanded) {
      this.drawingManager.setMap(null);
      this.drawingManager.setDrawingMode(null);
    } else {
      this.drawingManager.setMap(this.map);
    }

    if (this.myLocationControlBtnWrapper) {
      this.myLocationControlBtnWrapper.nativeElement.style.display = $expanded ? 'none' : 'block';
    }
  }

  public onAutocompleteResult($place: google.maps.places.PlaceResult): void {
    if ($place) {
      this.lat = $place.geometry.location.lat();
      this.lng = $place.geometry.location.lng();
      this.zoom = BaseMap.LOCATION_SEARCH_DEFAULT_ZOOM;

      this.map.panTo(new google.maps.LatLng(this.lat, this.lng));
      this.map.setZoom(this.zoom);
    }
  }

  public onWhat3WordsResult(what3WordInfo: What3WordInfo): void {
    this.lat = what3WordInfo.lat;
    this.lng = what3WordInfo.lng;
    this.zoom = BaseMap.W3W_SERCH_DEFAULT_ZOOM;
    this.map.setCenter({ lat: this.lat, lng: this.lng });
    this.map.setZoom(this.zoom);
    this.map.setMapTypeId('satellite');
  }

  public onVenueMapSelected(venueMapId: number): void {
    const venueMapsOverlay = [...this.groundOverlays, ...this.tileOverlays] as IVenueMapOverlay[];
    const venueMapOverlay = venueMapsOverlay.find(vm => vm.id === venueMapId);
    if(venueMapOverlay) {
      const level = venueMapOverlay.level;
      venueMapsOverlay.forEach(vm => {
        if(vm.level === level) {
          if(vm instanceof google.maps.GroundOverlay) {
            vm.setMap(this.map);
          } else if(vm instanceof google.maps.ImageMapType) {
            const overlayIndex = this.map.overlayMapTypes.getArray().indexOf(vm);
            if(overlayIndex === -1) {
              this.map.overlayMapTypes.insertAt(0, vm);
            }
          }
        } else {
          if(vm instanceof google.maps.GroundOverlay) {
            vm.setMap(null);
          } else if(vm instanceof google.maps.ImageMapType) {
            const overlayIndex = this.map.overlayMapTypes.getArray().indexOf(vm);
            if(overlayIndex !== -1) {
              this.map.overlayMapTypes.removeAt(overlayIndex);
            }
          }
        }
      });

      this.zones.forEach(z => {
        const polygon = this.zonePolygonMap.get(z.id);
        if(z.levelIndex === level) {
          polygon?.setMap(this.map);
        } else {
          polygon?.setMap(null);
        }
      });

      this.areas.forEach(a => {
        const polygon = this.areaPolygonMap.get(a.id);
        if(a.levelIndex === level) {
          polygon?.setMap(this.map);
        } else {
          polygon?.setMap(null);
        }
      });
    }
  }

  public onVenueMapSelectionCleared(): void {
    const venueMapsOverlay = [...this.groundOverlays, ...this.tileOverlays] as IVenueMapOverlay[];
    venueMapsOverlay.forEach(vm => {
      if(vm instanceof google.maps.GroundOverlay) {
        vm.setMap(this.map);
      } else if(vm instanceof google.maps.ImageMapType) {
        const overlayIndex = this.map.overlayMapTypes.getArray().indexOf(vm);
        if(overlayIndex === -1) {
          this.map.overlayMapTypes.insertAt(0, vm);
        }
      }
    });

    this.zones.forEach(z => {
      const polygon = this.zonePolygonMap.get(z.id);
      polygon?.setMap(this.map);
    });

    this.areas.forEach(a => {
      const polygon = this.areaPolygonMap.get(a.id);
      polygon?.setMap(this.map);
    });
  }

  private initControlsOverlay() {
    if(this.topRightControlsWrapper) {
      this.map.controls[google.maps.ControlPosition.RIGHT_TOP].push(this.topRightControlsWrapper.nativeElement);
    }

    if (this.topLeftControlsWrapper) {
      this.map.controls[google.maps.ControlPosition.LEFT_TOP].push(this.topLeftControlsWrapper.nativeElement);
    }

    if(this.topCenterControlsWrapper) {
      this.map.controls[google.maps.ControlPosition.TOP_CENTER].push(this.topCenterControlsWrapper.nativeElement);
    }

    if(this.bottomCenterControlsWrapper) {
      this.map.controls[google.maps.ControlPosition.BOTTOM_CENTER].push(this.bottomCenterControlsWrapper.nativeElement);
    }

    this.mapControlsReady = true;
    this.changeDetector.detectChanges();
  }

  private initOverlayTiles() {
    this.map.overlayMapTypes.insertAt(0, new DarkOverlayMapType(new google.maps.Size(256, 256)));
  }

  /**
   * Updates the opacity of the dark overlay on the map.
   * Finds the dark overlay in the overlayMapTypes array (assumed to be the last element)
   * and sets its opacity to the specified value.
   *
   * @param opacity - The opacity value to set for the overlay (typically between 0 and 1)
   * @returns void
   *
   * @remarks
   * This method assumes that the dark overlay is the last overlay in the overlayMapTypes array.
   * If no dark overlay is found or if the found overlay is not an instance of DarkOverlayMapType,
   * the method will not perform any action.
   */
  public updateOverlayOpacity(opacity: number): void {
    // Find the dark overlay in overlayMapTypes and update it
    // Assuming it's the last one in the list
    const darkOverlayIndex = this.map.overlayMapTypes.getLength() - 1;
    if (darkOverlayIndex >= 0) {
      const currentOverlay = this.map.overlayMapTypes.getAt(darkOverlayIndex);
      if (currentOverlay instanceof DarkOverlayMapType) {
        currentOverlay.setOpacity(opacity);
      }
    };

    this.updateGroundOverlaysBrightness(opacity);
  }

  private initDrawingManager() {
    this.drawingManager = new google.maps.drawing.DrawingManager({
      drawingMode: google.maps.drawing.OverlayType.MARKER,
      drawingControl: false,
      drawingControlOptions: {
        position: google.maps.ControlPosition.TOP_CENTER,
        drawingModes: [google.maps.drawing.OverlayType.POLYGON],
      },
      polygonOptions: {
        strokeColor: this.getPolygonDrawingColor(),
        strokeOpacity: 1,
        strokeWeight: 2,
        fillColor: this.getPolygonDrawingColor(),
        fillOpacity: 0.2,
        clickable: true,
        draggable: true,
        editable: true,
      },
    });
    this.drawingManager.setMap(this.map);
    this.drawingManager.setDrawingMode(null);

    this.drawingManager.addListener('polygoncomplete', (polygon: google.maps.Polygon) => {
      this.newDrawingZonePolygon = polygon;
      this.drawingManager.setDrawingMode(null);

      this.map.fitBounds(GMapUtilities.getPolygonLatLngBounds(polygon));
      this.registerPolygonEvents(this.newDrawingZonePolygon);
      this.handlePolygonBoundariesChange(polygon);

      this.mapControlsReady = true;
      this.changeDetector.detectChanges();
    });
    google.maps.event.addListener(this.drawingManager, 'drawingmode_changed', (e) => {
      if(this.drawingManager.getDrawingMode() === "polygon") {
        this.mapControlsReady = false;
      } else if(this.drawingManager.getDrawingMode() === null) {
        this.mapControlsReady = true;
      }

      this.changeDetector.detectChanges();
    });
  }

  private registerPolygonEvents(polygon: google.maps.Polygon): void {
    polygon.addListener('click', (event: google.maps.PolyMouseEvent) => {
      const clickSport = event.vertex;
      if (typeof clickSport === 'number') {
        const clickedVertex: number = clickSport;
        const path = polygon.getPath();
        if (path.getLength() > ZoneMapComponent.MIN_POLYGON_VERTEX) {
          path.removeAt(clickedVertex);

          const polyLatLngBounds = GMapUtilities.getPolygonLatLngBounds(polygon);
          this.map.fitBounds(polyLatLngBounds);

          //this.zonePolygonUpdated.emit(this.getIncidentZonePolygon(polygon));
        }
      }
    });

    google.maps.event.addListener(polygon, 'dragstart', (_event: google.maps.MapMouseEvent) => {
      this.disableMVCArraySetAtEventSubmitter = true;
    });
    google.maps.event.addListener(polygon, 'dragend', (_event: google.maps.MapMouseEvent) => {
      this.disableMVCArraySetAtEventSubmitter = false;
      this.handlePolygonBoundariesChange(polygon);
    });

    polygon.getPaths().forEach((path, _index) => {
      google.maps.event.addListener(path, 'insert_at', () => {
        this.handlePolygonBoundariesChange(polygon);
      });

      google.maps.event.addListener(path, 'remove_at', () => {
        this.handlePolygonBoundariesChange(polygon);
      });

      google.maps.event.addListener(path, 'set_at', () => {
        if (!this.disableMVCArraySetAtEventSubmitter) {
          this.handlePolygonBoundariesChange(polygon);
        }
      });
    });
  }

  private handlePolygonBoundariesChange(polygon: google.maps.Polygon): void {
    const validShape = this.validateDrawingPolygonBoundaries();
    polygon.setOptions({
      strokeColor: validShape ? this.getPolygonDrawingColor() : 'red'
    });

    this.zonePolygonUpdated.emit(this.getPolygonViewModel(polygon));
  }

  /**
   * When a new polygon is drawn on the map or the shape of an exisitng one is modified
   * this function checks if the boundaries are compliant with the following rules:
   *
   * 1. In case the map editing mode is set to ZONE:
   * The function will return true if the boundaries of the polygon contain ALL the vertexes
   * of the polygons of the areas belonging to the zone.
   *
   * 2. In case the map editing mode is set to AREA:
   * The function will return true if ALL the vertexes of the area are inside the boundaries
   * of the polygon of the zone to which it belongs.
   *
   * @returns true if the vertexes of the zone/area which is being drawn on the map are not
   * outside of the parent polygon. Otherwise false is returned.
   */
  private validateDrawingPolygonBoundaries(): boolean {
    let result = true;

    if(this.polygonInput) {
      if(this.polygonDrawingMode === PolygonType.ZONE) {
        const outerVertexFound = this.areas
          .filter(a => a.zoneId === this.polygonInput.id)
          .some(a => {
            return this.areaPolygonMap.get(a.id).getPath().getArray()
              .some(latLng => {
                return !google.maps.geometry.poly.containsLocation(latLng, this.newDrawingZonePolygon);
              });
          });

        result = !outerVertexFound;
      }
    } else {
      if(this.polygonDrawingMode === PolygonType.AREA) {
        const outerVertexFound = this.newDrawingZonePolygon.getPath().getArray()
          .some(latLng => {
            return !google.maps.geometry.poly.containsLocation(latLng, this.zonePolygonMap.get(this.focusZoneId));
          });

        result = !outerVertexFound;
      }
    }

    return result;
  }

  private displayInputPolygon() {
    if (this.polygonInput) {
      const polygon =
        this.polygonDrawingMode === PolygonType.ZONE
          ? this.zonePolygonMap.get(this.polygonInput.id)
          : this.areaPolygonMap.get(this.polygonInput.id);

      if (polygon) {
        polygon.setOptions({
          editable: true,
          draggable: true,
          clickable: true,
        });

        this.newDrawingZonePolygon = polygon;
        this.registerPolygonEvents(this.newDrawingZonePolygon);

        const polyLatLngBounds = GMapUtilities.getPolygonLatLngBounds(this.newDrawingZonePolygon);
        this.map.panToBounds(polyLatLngBounds);
        this.map.fitBounds(polyLatLngBounds);
      }
    }
  }

  private getPolygonViewModel(polygon: google.maps.Polygon): IPolygonLike {
    const latLngPath: ShapePointViewModel[] = [];
    if (polygon !== null) {
      const vertexesArray = polygon.getPath().getArray();
      vertexesArray.forEach((latLng) => {
        latLngPath.push(new ShapePointViewModel(latLng.lat(), latLng.lng()));
      });
    }

    if (this.polygonDrawingMode === PolygonType.ZONE) {
      const zonePolygon = new ZonePolygon(this.polygonInput?.id || 0, latLngPath);
      return zonePolygon;
    } else {
      const areaPolygon = new AreaPolygon(0, latLngPath);
      return areaPolygon;
    }
  }

  private pantToMyLocation(): void {
    if (this.myLocationLatLng) {
      this.map.panTo(this.myLocationLatLng);
      this.map.setZoom(BaseMap.MY_LOCATION_DEFAULT_ZOOM);
    }
  }

  private getPolygonDrawingColor(): string {
    return this.polygonDrawingMode === PolygonType.ZONE
      ? GMapUtilities.DEFAULT_ZONE_POLYGON_COLOR
      : GMapUtilities.DEFAULT_AREA_POLYGON_COLOR;
  }

  private processZonesAndAreas() {
    super.getZonesAndAreas((zones, areas, venueMaps) => {
      this.zones = zones;
      this.areas = areas;
      this.venueMaps = venueMaps.filter(vm => vm.active);
      super.buildPolygonsFromZonesAndAreas(this.zones, this.areas, true);
      super.buildVenueMaps(this.venueMaps, true);

      if(this.polygonInput && this.polygonInput.shapePoints.length) {
        this.displayInputPolygon();
      } else {
        if(this.onZonesPolygonCreated) {
          this.onZonesPolygonCreated();
          this.onZonesPolygonCreated = null;
        } else {
          this.map.fitBounds(GMapUtilities.getPolygonsLatLngBounds(
            Array.from(this.zonePolygonMap.values()))
          );
        }
      }

      //this.updateOverlayOpacity(this.overlayOpacity);

      this.drawablesLoaded = true;
      this.changeDetector.detectChanges();
    });
  }
}
